import * as React from "react";
import { createPortal } from "react-dom";
import { Package, Search, Plus, Download, Trash2, Edit2, Tag, MapPin, CheckSquare, Square, ChevronDown, Check, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { AddItemModal } from "./AddItemModal.js";
import { ItemDetailModal } from "./ItemDetailModal.js";
import { BulkEditModal } from "./BulkEditModal.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { useToast } from "@/components/Toast.js";

import { useSearch, useLocation } from "wouter";
import { AdSlot } from "@/components/AdSlot.js";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Item {
  id: string; name: string; description: string | null; quantity: number; status: string | null;
  manufacturer: string | null; purchaseDate: string | null; careInstructions: string | null; lastUsedDate: string | null;
  spaceId: string | null; spaceName: string | null;
  tags: string[]; photoUrl: string | null; notes: string | null; estimatedValue: number | null;
  receiptPhotoUrl: string | null; warrantyPhotoUrl: string | null;
  createdAt: string; updatedAt: string;
}

const PAGE_SIZE = 50;

export default function InventoryPage() {
  const searchString = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(searchString);
  const action = params.get("action");
  const highlightId = params.get("highlight");
  const initialSpaceId = params.get("spaceId") || undefined;
  const toast = useToast();

  const [items, setItems] = React.useState<Item[]>([]);
  const [search, setSearch] = React.useState("");
  const [sortBy, setSortBy] = React.useState("newest");
  const [filterTag, setFilterTag] = React.useState<string[]>([]);
  const [filterSpace, setFilterSpace] = React.useState<string[]>(initialSpaceId ? [String(initialSpaceId)] : []);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(action === "add" || action === "scan");
  const [addTab, setAddTab] = React.useState<"photo" | "text" | undefined>(action === "scan" ? "photo" : undefined);
  const [detailItem, setDetailItem] = React.useState<Item | null>(null);
  const [showBulkEdit, setShowBulkEdit] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showSortDropdown, setShowSortDropdown] = React.useState(false);
  const sortDropdownRef = React.useRef<HTMLDivElement>(null);
  const { spaces, refetch: refetchSpaces } = useSpaces();

  const fetchItems = React.useCallback(async (pageNum = 1, append = false) => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (sortBy) p.set("sort", sortBy);
    if (filterTag.length) p.set("tags", filterTag.join(","));
    if (filterSpace.length) p.set("spaceId", filterSpace.join(","));
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String((pageNum - 1) * PAGE_SIZE));
    setLoadError(false);
    try {
      const data = await apiFetch<Item[]>(`/api/items?${p}`);
      setItems(append ? (prev) => [...prev, ...data] : data);
      setHasMore(data.length === PAGE_SIZE);
      setPage(pageNum);
    } catch {
      if (!append) setLoadError(true);
      else toast.show("Failed to load more items", "error");
    }
    setLoading(false);
  }, [search, sortBy, filterTag, filterSpace]);

  React.useEffect(() => { setPage(1); fetchItems(1); }, [fetchItems]);

  // Open item detail when navigating from search with ?highlight=ID
  React.useEffect(() => {
    if (!highlightId) return;
    const found = items.find((i) => i.id === highlightId);
    if (found) { setDetailItem(found); navigate("/inventory", { replace: true }); return; }
    // Item may not be in current page — fetch it directly
    apiFetch<Item>(`/api/items/${highlightId}`).then((item) => {
      setDetailItem(item);
      navigate("/inventory", { replace: true });
    }).catch(() => {});
  }, [highlightId, items]);

  const allTags = React.useMemo(() => [...new Set(items.flatMap((i) => i.tags ?? []))], [items]);

  // Build display names for spaces, disambiguating duplicates by parent (floor) name
  const spaceDisplayName = React.useMemo(() => {
    const roomSpaces = spaces.filter((s) => s.type === "room" || s.type === "container");
    const nameCount: Record<string, number> = {};
    roomSpaces.forEach((s) => { nameCount[s.name] = (nameCount[s.name] || 0) + 1; });
    const parentMap: Record<string, string> = {};
    spaces.forEach((s) => { parentMap[String(s.id)] = s.name; });
    const displayMap: Record<string, string> = {};
    roomSpaces.forEach((s) => {
      const sid = String(s.id);
      if (nameCount[s.name] > 1 && s.parentId && parentMap[String(s.parentId)]) {
        displayMap[sid] = `${s.name} (${parentMap[String(s.parentId)]})`;
      } else {
        displayMap[sid] = s.name;
      }
    });
    return displayMap;
  }, [spaces]);

  const [showLocationPicker, setShowLocationPicker] = React.useState(false);
  const locationBtnRef = React.useRef<HTMLButtonElement>(null);
  const [locationDropdownStyle, setLocationDropdownStyle] = React.useState<React.CSSProperties>({});

  // Position and close location picker
  React.useEffect(() => {
    if (!showLocationPicker) return;
    if (locationBtnRef.current) {
      const rect = locationBtnRef.current.getBoundingClientRect();
      setLocationDropdownStyle({ position: "fixed", top: rect.bottom + 4, left: rect.left, zIndex: 9999 });
    }
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (locationBtnRef.current?.contains(target)) return;
      const dropdown = document.getElementById("location-picker-dropdown");
      if (dropdown?.contains(target)) return;
      setShowLocationPicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLocationPicker]);

  // Close sort dropdown on outside click
  React.useEffect(() => {
    if (!showSortDropdown) return;
    const handler = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) setShowSortDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSortDropdown]);

  const toggleSelect = (id: string) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === items.length ? [] : items.map((i) => i.id));

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      await apiFetch("/api/items/bulk", { method: "DELETE", body: JSON.stringify({ ids }) });
      toast.show(`${ids.length} items deleted`, "success", {
        label: "Undo",
        onClick: async () => {
          try {
            await apiFetch("/api/items/bulk/restore", { method: "POST", body: JSON.stringify({ ids }) });
            toast.show(`${ids.length} items restored!`);
            fetchItems(1);
          } catch { toast.show("Failed to restore", "error"); }
        },
      });
      setSelectedIds([]); setConfirmDelete(false); fetchItems(1);
    } catch (e: any) { toast.show(e.message ?? "Failed to delete items", "error"); }
  };

  const handleExportCsv = () => { window.open("/api/items/export/csv", "_blank"); };

  const loadMore = () => { if (hasMore) fetchItems(page + 1, true); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 font-headline"><Package className="w-6 h-6 text-primary" />Inventory</h1>
        <div className="flex gap-2">
          <button onClick={handleExportCsv} className="px-3 py-1.5 rounded-lg bg-primary-light text-primary text-xs font-medium flex items-center gap-1"><Download className="w-3.5 h-3.5" />CSV</button>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium flex items-center gap-1"><Plus className="w-4 h-4" />Add Item</button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input aria-label="Search items" placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-border/40 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
      </div>

      {/* Unplaced / Re-locate entry points (P0-B, R-B3 — same lists, reachable
          from Inventory as well as Organize). */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => navigate("/organize/lists?list=unplaced")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amethyst-soft text-amethyst-ink text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
        >
          <MapPin className="w-3.5 h-3.5" />Unplaced
        </button>
        <button
          onClick={() => navigate("/organize/lists?list=relocate")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amethyst-soft text-amethyst-ink text-xs font-medium hover:bg-amethyst-soft/70 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />Re-locate
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center flex-wrap">
        {/* Sort dropdown */}
        <div className="relative" ref={sortDropdownRef}>
          <button onClick={() => setShowSortDropdown(!showSortDropdown)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-gray-600 flex items-center gap-1 whitespace-nowrap">
            Sort: <span className="capitalize">{sortBy}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {showSortDropdown && (
              <div className="absolute left-0 top-full mt-1 w-36 bg-white rounded-xl border border-neutral-border/30 shadow-lg z-50 py-1">
                {["newest", "oldest", "name", "location"].map((s) => (
                  <button key={s} onClick={() => { setSortBy(s); setShowSortDropdown(false); }} className={cn("w-full text-left px-3 py-2 text-xs capitalize", sortBy === s ? "bg-amethyst-soft text-amethyst-ink font-medium" : "text-gray-600 hover:bg-gray-50")}>{s}</button>
                ))}
              </div>
          )}
        </div>

        {/* Location filter */}
        <div className="relative">
          <button
            ref={locationBtnRef}
            onClick={() => setShowLocationPicker(!showLocationPicker)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1",
              filterSpace.length > 0 ? "bg-primary text-white" : "bg-muted text-gray-600"
            )}
          >
            <MapPin className="w-3 h-3" />
            {filterSpace.length > 0
              ? `${spaceDisplayName[filterSpace[0]] ?? "Location"}${filterSpace.length > 1 ? ` +${filterSpace.length - 1}` : ""}`
              : "Location"}
            <ChevronDown className="w-3 h-3" />
          </button>
          {showLocationPicker && createPortal(
            <div id="location-picker-dropdown" style={locationDropdownStyle} className="bg-white rounded-xl border border-neutral-border/30 shadow-lg p-2 min-w-[200px] max-h-60 overflow-y-auto">
              {spaces.filter((s) => s.type === "room" || s.type === "container").length === 0 ? (
                <p className="text-xs text-gray-400 px-2 py-1">No spaces created yet</p>
              ) : (
                <>
                  {filterSpace.length > 0 && (
                    <button
                      onClick={() => { setFilterSpace([]); setShowLocationPicker(false); }}
                      className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 mb-1"
                    >
                      Clear selection
                    </button>
                  )}
                  {spaces.filter((s) => s.type === "room" || s.type === "container").map((space) => {
                    const sid = String(space.id);
                    const active = filterSpace.includes(sid);
                    return (
                      <button
                        key={space.id}
                        onClick={() => { setFilterSpace(active ? filterSpace.filter(x => x !== sid) : [...filterSpace, sid]); }}
                        className={cn(
                          "w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2",
                          active ? "bg-amethyst-soft text-amethyst-ink font-medium" : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <MapPin className="w-3 h-3" />
                        {spaceDisplayName[sid] ?? space.name}
                        {active && <Check className="w-3 h-3 ml-auto" />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>,
            document.body
          )}
        </div>

        {/* Tag filter — searchable popover */}
        <TagFilterPopover allTags={allTags} filterTag={filterTag} setFilterTag={setFilterTag} />

        {/* Clear all */}
        {(filterTag.length > 0 || filterSpace.length > 0) && (
          <button onClick={() => { setFilterTag([]); setFilterSpace([]); }} className="px-3 py-1.5 rounded-full text-xs font-medium text-red-500 bg-red-50">Clear</button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amethyst-soft border border-clay-border">
          <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
          <div className="flex-1" />
          <button onClick={toggleSelectAll} className="px-3 py-1 rounded-lg text-xs font-medium bg-white border border-neutral-border/30">
            {selectedIds.length === items.length ? "Deselect All" : "Select All"}
          </button>
          <button onClick={() => setShowBulkEdit(true)} className="px-3 py-1 rounded-lg text-xs font-medium bg-primary text-white flex items-center gap-1"><Edit2 className="w-3 h-3" />Edit</button>
          <button onClick={() => setConfirmDelete(true)} className="px-3 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
        </div>
      )}

      {/* Items count */}
      {!loading && items.length > 0 && (
        <p className="text-xs text-gray-500" aria-live="polite">{items.length} items</p>
      )}

      {/* Items list */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
      ) : loadError ? (
        <div className="text-center py-20">
          <p className="text-gray-400 mb-4">Failed to load items</p>
          <button onClick={() => fetchItems(1)} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-1 font-medium">No items yet</p>
          <p className="text-gray-400 text-sm mb-5">Start by adding items you own. Snap a photo or type a name.</p>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium">Add your first item</button>
        </div>
      ) : (
        <VirtualizedItemList items={items} selectedIds={selectedIds} toggleSelect={toggleSelect} setDetailItem={setDetailItem} hasMore={hasMore} loadMore={loadMore} />
      )}

      {/* Modals */}
      <AddItemModal open={showAdd} onClose={() => { setShowAdd(false); setAddTab(undefined); navigate("/inventory", { replace: true }); }} spaces={spaces} onSaved={(nudge) => {
        fetchItems(1);
        // F-a: item added without a home → offer a one-tap deep link into
        // Find-a-home, prefilled for this item (reuses the ?suggest_item entry).
        if (nudge?.needsHome) {
          toast.show("Item added — no home yet.", "success", {
            label: "Find a home",
            onClick: () => navigate(`/organize/goals?suggest_item=${nudge.itemId}`),
          });
        } else {
          toast.show("Item added!");
        }
      }} initialTab={addTab} initialSpaceId={initialSpaceId} onSpaceCreated={refetchSpaces} />
      <ItemDetailModal item={detailItem} onClose={() => setDetailItem(null)} spaces={spaces} onUpdated={() => { fetchItems(1); }} onSpaceCreated={refetchSpaces} />
      <BulkEditModal open={showBulkEdit} onClose={() => { setShowBulkEdit(false); setSelectedIds([]); }} selectedIds={selectedIds} spaces={spaces} onUpdated={() => { toast.show("Items updated!"); fetchItems(1); }} onSpaceCreated={refetchSpaces} />
      <ConfirmDialog open={confirmDelete} title="Delete Items" message={`Delete ${selectedIds.length} selected items? This cannot be undone.`} confirmLabel="Delete" destructive onConfirm={handleBulkDelete} onCancel={() => setConfirmDelete(false)} />
      <AdSlot slot="inventory-bottom" />
    </div>
  );
}

const ITEM_HEIGHT = 76; // approximate row height in px

/* Searchable tag filter popover */
function TagFilterPopover({ allTags, filterTag, setFilterTag }: { allTags: string[]; filterTag: string[]; setFilterTag: (tags: string[]) => void }) {
  const [open, setOpen] = React.useState(false);
  const [tagSearch, setTagSearch] = React.useState("");
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = tagSearch.trim()
    ? allTags.filter((t) => t.toLowerCase().includes(tagSearch.toLowerCase()))
    : allTags;

  if (allTags.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setTagSearch(""); }}
        className={cn(
          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1",
          filterTag.length > 0 ? "bg-primary text-white" : "bg-muted text-gray-600"
        )}
      >
        <Tag className="w-3 h-3" />
        {filterTag.length > 0 ? `${filterTag[0]}${filterTag.length > 1 ? ` +${filterTag.length - 1}` : ""}` : "Tags"}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-xl border border-neutral-border/30 shadow-lg z-50 p-2">
          <input
            autoFocus
            placeholder="Search tags..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-neutral-border/30 text-xs mb-2 focus:outline-none focus:border-primary"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filterTag.length > 0 && (
              <button onClick={() => { setFilterTag([]); setOpen(false); }} className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50">Clear tags</button>
            )}
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-1">No matching tags</p>
            ) : (
              filtered.map((tag) => {
                const active = filterTag.includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => setFilterTag(active ? filterTag.filter((t) => t !== tag) : [...filterTag, tag])}
                    className={cn(
                      "w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2",
                      active ? "bg-amethyst-soft text-amethyst-ink font-medium" : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                    {active && <Check className="w-3 h-3 ml-auto" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function VirtualizedItemList({ items, selectedIds, toggleSelect, setDetailItem, hasMore, loadMore }: {
  items: Item[]; selectedIds: string[]; toggleSelect: (id: string) => void;
  setDetailItem: (item: Item) => void; hasMore: boolean; loadMore: () => void;
}) {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const [maxH, setMaxH] = React.useState(400);
  const count = items.length + (hasMore ? 1 : 0);
  const virtualizer = useVirtualizer({ count, getScrollElement: () => parentRef.current, estimateSize: () => ITEM_HEIGHT, overscan: 5 });

  React.useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setMaxH(Math.max(200, window.innerHeight - rect.top - 24));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <div ref={parentRef} className="overflow-auto" style={{ maxHeight: maxH }}>
      <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
        {virtualizer.getVirtualItems().map((vRow) => {
          if (vRow.index === items.length) {
            return (
              <div key="load-more" style={{ position: "absolute", top: vRow.start, left: 0, right: 0, height: vRow.size }} className="flex items-center justify-center">
                <button onClick={loadMore} className="w-full py-3 rounded-xl bg-gray-50 text-sm text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">Load More</button>
              </div>
            );
          }
          const item = items[vRow.index];
          const selected = selectedIds.includes(item.id);
          return (
            <div key={item.id} style={{ position: "absolute", top: vRow.start, left: 0, right: 0, height: vRow.size, padding: "4px 0" }}>
              {/* Row = a real button (keyboard + SR operable). The select checkbox
                  is a SIBLING control, not nested inside the row button, since
                  interactive elements must not nest. */}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-neutral-border/20 hover:border-primary/30 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 transition-all h-full">
                <button
                  type="button"
                  onClick={() => toggleSelect(item.id)}
                  role="checkbox"
                  aria-checked={selected}
                  aria-label={selected ? `Deselect ${item.name}` : `Select ${item.name}`}
                  className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {selected ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-gray-400" />}
                </button>
                <button
                  type="button"
                  onClick={() => setDetailItem(item)}
                  aria-label={`View details for ${item.name}`}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {item.photoUrl ? <img src={item.photoUrl} alt="" loading="lazy" className="w-12 h-12 rounded-xl object-cover shrink-0" /> : <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-gray-400" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2"><h3 className={`font-medium text-sm truncate ${item.quantity === 0 ? "italic text-gray-400" : ""}`}>{item.name}</h3><span className={`text-xs shrink-0 ${item.quantity === 0 ? "italic text-gray-400" : "text-gray-500"}`}>{item.quantity === 0 ? "empty" : `x${item.quantity}`}</span></div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {item.spaceName && <span className="text-xs px-2 py-0.5 rounded-full bg-amethyst-soft text-amethyst-ink flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{item.spaceName}</span>}
                      {(item.tags ?? []).map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t}</span>)}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
